// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProp, resolveColor } from "@insite/mobius/utilities";
import { css } from "styled-components";

/**
 * Add custom style for scrollbar
 * @param width
 */
export const styledScrollbar = ({
    width = 8,
    background,
    margined,
}: { width?: number; background?: any; margined?: number } = {}) => {
    return ({ theme }: any) => {
        const trackBG: string = background || resolveColor("common.white", theme);
        const thumbColor = resolveColor("common.border", theme);

        // webkit-scrollbar-track to add scrollbar margin
        const withMarginTopBottom = margined
            ? css`
                  &::-webkit-scrollbar-track {
                      margin-block: ${margined}px;
                  }
              `
            : "";

        return css`
            overflow-y: auto;
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            @supports not selector(::-webkit-scrollbar) {
                scrollbar-width: thin;
                scrollbar-color: ${resolveColor(thumbColor, theme)} ${resolveColor(trackBG, theme)};
            }
            &::-webkit-scrollbar,
            &::-webkit-scrollbar:hover {
                width: ${width}px;
                height: ${width}px;
            }
            &::-webkit-scrollbar-track {
                background: ${trackBG};
                border-radius: 4px;
            }
            &::-webkit-scrollbar-thumb {
                background-color: ${thumbColor};
                border-radius: 4px;
                border: ${width / 4}px solid ${trackBG};
            }
            &::-webkit-scrollbar-thumb:hover {
                background: ${resolveColor("text.link", theme)};
            }
            ${withMarginTopBottom}
        `;
    };
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* this prevent scroll outside the container */
    overscroll-behavior-y: contain;
`;

const hasVScroll = () => document.body.scrollHeight > document.body.clientHeight;

export const addScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.style.setProperty("scrollbar-gutter", "stable");
};

export const removeScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.style.removeProperty("scrollbar-gutter");
};

/**
 * Scroll to invalid field
 * @param scrollToTop
 */
export const doScroll = (scrollToTop = false) => {
    setTimeout(
        () => {
            const selector = document.querySelector("[aria-invalid='true']");

            if (selector) {
                selector.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                return;
            }

            if (scrollToTop) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });
                return;
            }

            doScroll(scrollToTop);
        },
        scrollToTop ? 10 : 500,
    );
};
